import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import {
  closeBillReleaseLinkedModal,
  type BillReleaseLinkedModalContext,
} from "../state/slices/billReleaseLinkedModalSlice";
import { apiService } from "../services/api";
import { useDarkMode } from "../contexts/DarkModeContext";

type LinkedRow = {
  source_voucher_no?: string;
  source_type?: string;
  allocated_amount?: number;
  released_amount?: number;
  remaining_amount?: number;
  status?: string;
  created_at?: string;
};

const formatMoney = (value: unknown): string => {
  const n = parseFloat(String(value ?? "0").replace(/,/g, ""));
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

export const BillReleaseLinkedTransactionsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, context } = useAppSelector((s) => s.billReleaseLinkedModal);
  const { isDarkMode } = useDarkMode();
  const [rows, setRows] = useState<LinkedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLinkedRows = useCallback(async (ctx: BillReleaseLinkedModalContext) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getModuleData(
        "bill_release_linked_allocations",
        {
          view: "view_bill_release_linked_allocations",
          skip: 0,
          limit: 200,
          search: [
            {
              name: "party_id",
              value: ctx.partyId,
              controltype: "TextView",
            },
            {
              name: "document_no",
              value: ctx.documentNo,
              controltype: "TextView",
            },
            {
              name: "party_side",
              value: ctx.partySide,
              controltype: "TextView",
            },
          ],
          sort: [{ field: "created_at", direction: "desc" }],
        }
      );
      setRows(Array.isArray(response.data) ? (response.data as LinkedRow[]) : []);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load linked transactions."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && context) {
      void loadLinkedRows(context);
    } else {
      setRows([]);
      setError(null);
    }
  }, [isOpen, context, loadLinkedRows]);

  if (!isOpen || !context) {
    return null;
  }

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const panelStyle: React.CSSProperties = {
    width: "min(960px, 100%)",
    maxHeight: "85vh",
    overflow: "auto",
    backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
    color: isDarkMode ? "#f8fafc" : "#0f172a",
    borderRadius: 12,
    boxShadow: "0 20px 45px rgba(15, 23, 42, 0.25)",
    padding: 20,
  };

  return (
    <div style={overlayStyle} onClick={() => dispatch(closeBillReleaseLinkedModal())}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              Linked transactions
            </h3>
            <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.8 }}>
              {context.documentNo} · {context.partyName || context.partyId}
            </p>
          </div>
          <button
            type="button"
            onClick={() => dispatch(closeBillReleaseLinkedModal())}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              cursor: "pointer",
              color: "inherit",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {loading ? <p>Loading linked allocations…</p> : null}
        {error ? <p style={{ color: "#dc2626" }}>{error}</p> : null}

        {!loading && !error && rows.length === 0 ? (
          <p>No active allocation links found for this document.</p>
        ) : null}

        {rows.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    "Voucher No.",
                    "Type",
                    "Allocated",
                    "Released",
                    "Remaining",
                    "Status",
                    "Date",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderBottom: isDarkMode
                          ? "1px solid #334155"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`${row.source_voucher_no}-${idx}`}>
                    <td style={{ padding: "8px 10px" }}>
                      {row.source_voucher_no || "—"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>{row.source_type || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      {formatMoney(row.allocated_amount)}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {formatMoney(row.released_amount)}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {formatMoney(row.remaining_amount)}
                    </td>
                    <td style={{ padding: "8px 10px" }}>{row.status || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>
                      {row.created_at
                        ? String(row.created_at).slice(0, 10)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
};
